if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(await this.getBundleStockMessage(response.description, formData));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      async getBundleStockMessage(errorMessage, formData) {
        if (
          typeof errorMessage !== 'string' ||
          !this.dataset.bundleStockMessage ||
          !this.dataset.bundleStockUrl ||
          !/\bis already sold out\b|Only \d+ items? (?:was|were) added to your cart due to availability/i.test(errorMessage)
        ) return errorMessage;

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 4000);
        try {
          const url = new URL(this.dataset.bundleStockUrl, window.location.origin);
          url.searchParams.set('variant', formData.get('id'));
          url.searchParams.set('section_id', 'have-a-scent-stock');
          url.searchParams.set('_stock_check', Date.now().toString());
          const response = await fetch(url, { cache: 'no-store', signal: controller.signal });
          if (!response.ok) return errorMessage;
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const payload = html.querySelector('[data-bundle-stock-payload]');
          if (!payload) return errorMessage;
          const data = JSON.parse(payload.getAttribute('data-bundle-stock-payload'));
          const current = new FormData(this.form);
          const unchanged = ['id', 'quantity', ...(data.optionNames || []).map(name => `properties[${name}]`)]
            .every(name => JSON.stringify(current.getAll(name)) === JSON.stringify(formData.getAll(name)));
          if (!unchanged) return false;
          return this.describeBundleShortage(data, formData) || errorMessage;
        } catch {
          return errorMessage;
        } finally {
          clearTimeout(timeout);
        }
      }

      describeBundleShortage(data, formData) {
        if (
          !data.complete ||
          String(data.variantId) !== String(formData.get('id')) ||
          !Array.isArray(data.components) || data.components.length !== data.componentCount ||
          !Array.isArray(data.optionNames) || !data.optionNames.length ||
          !Array.isArray(data.cart) || !data.messages
        ) return null;

        const quantity = Number(formData.get('quantity') || 1);
        if (!Number.isSafeInteger(quantity) || quantity < 1) return null;
        const selected = new Map();
        for (const name of data.optionNames) {
          const values = formData.getAll(`properties[${name}]`);
          if (!values.length || !values[0] || values.some(value => value !== values[0])) return null;
          selected.set(name, values[0]);
        }

        const cartCounts = new Map();
        for (const item of data.cart) {
          if (!Array.isArray(item.components) || item.components.length !== (item.componentCount || 0)) return null;
          if (item.productId === data.productId && !item.components.length) return null;
          const lines = item.components.length ? item.components : [item];
          for (const line of lines) {
            if (!Number.isSafeInteger(line.quantity) || line.quantity < 1 || !line.id) return null;
            const id = String(line.id);
            cartCounts.set(id, (cartCounts.get(id) || 0) + line.quantity);
          }
        }

        const matchedOptions = new Map();
        const shortages = [];
        for (const component of data.components) {
          if (!Array.isArray(component.selections) || component.selections.length !== component.selectionCount) return null;
          let perBundle = 0;
          for (const selection of component.selections) {
            if (!Array.isArray(selection.options) || selection.options.length !== 1 || selection.quantity !== 1) return null;
            const option = selection.options[0];
            if (selected.get(option.name) !== option.value) continue;
            if (matchedOptions.has(option.name)) return null;
            matchedOptions.set(option.name, String(component.id));
            perBundle += 1;
          }
          if (!perBundle || !component.management || component.policy !== 'deny') continue;
          if (!Number.isSafeInteger(component.stock)) return null;
          const inCart = cartCounts.get(String(component.id)) || 0;
          const stock = component.available ? Math.max(0, component.stock) : 0;
          const remaining = Math.max(0, stock - inCart);
          const requested = perBundle * quantity;
          if (requested <= remaining) continue;
          shortages.push({
            scent: component.name,
            remaining,
            selected: requested,
            cart: inCart,
            swap: Math.ceil((requested - remaining) / quantity),
            quantity,
          });
        }
        if (matchedOptions.size !== selected.size || !shortages.length) return null;

        const format = (key, values) => {
          const template = data.messages[key];
          if (typeof template !== 'string' || !template || template.includes('Translation missing')) throw new Error('Missing stock message');
          return template.replace(/__(SCENT|REMAINING|SELECTED|CART|SWAP|QUANTITY)__/g, (_, token) => String(values[token.toLowerCase()]));
        };
        return shortages.map(values => {
          const messages = [format(values.remaining === 0 ? 'available_none' : values.remaining === 1 ? 'available_one' : 'available_many', values)];
          if (values.cart) messages.push(format(values.cart === 1 ? 'cart_one' : 'cart_many', values));
          messages.push(format(quantity > 1 ? 'swap_multiple' : values.swap === 1 ? 'swap_one' : 'swap_many', values));
          return messages.join(' ');
        }).join(' ');
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        if (
          typeof errorMessage === 'string' &&
          this.dataset.bundleStockMessage &&
          /\bis already sold out\b|Only \d+ items? (?:was|were) added to your cart due to availability/i.test(errorMessage)
        ) {
          errorMessage = this.dataset.bundleStockMessage;
        }

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}


